import React from 'react';

/**
 * The last line of defence for a render crash.
 *
 * THREE PROBLEMS WITH THE OLD ONE
 * -------------------------------
 * 1. It stuck across navigation. Once tripped it stayed tripped, so the user had
 *    to reload the whole app to reach any other screen. It now resets when the
 *    location changes, which makes "go somewhere else" a real escape route.
 * 2. "Try Again" cleared the error and re-rendered the same broken subtree, so it
 *    almost always failed again immediately. Retry is still offered — a transient
 *    render error does exist — but the primary action is now navigation.
 * 3. It hid the diagnostics outside development. Support then had a screenshot
 *    saying "Something went wrong" and nothing else. The details are behind a
 *    disclosure and copyable in every environment, because the person who needs
 *    them is the customer relaying them to us.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, path: window.location.pathname, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  componentDidUpdate() {
    // Reset on navigation. Without this the boundary outlives the broken screen.
    if (this.state.error && window.location.pathname !== this.state.path) {
      this.setState({ error: null, info: null, path: window.location.pathname, copied: false });
    }
  }

  report = () => {
    const { error, info } = this.state;
    const text = [
      `Screen: ${window.location.pathname}${window.location.search}`,
      `Time: ${new Date().toISOString()}`,
      `Error: ${error?.toString() || 'unknown'}`,
      info?.componentStack ? `Stack:${info.componentStack}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => this.setState({ copied: true }),
        () => this.setState({ copied: false })
      );
    }
  };

  render() {
    const { error, info, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg rounded-lg border border-line bg-surface p-6">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-bad-tint">
            <i className="fas fa-triangle-exclamation text-bad-text" aria-hidden="true" />
          </div>
          <h1 className="text-heading-sm text-ink-900">This screen stopped working</h1>
          <p className="mt-2 text-body text-ink-700">
            Your data is fine - this is a problem displaying it. Other screens should
            still work, so the quickest way forward is usually to go somewhere else
            and come back.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href="/"
              className="inline-flex h-[34px] items-center rounded-md bg-brand px-3 text-label font-medium text-brand-fg"
            >
              Go to Overview
            </a>
            <button
              type="button"
              onClick={() => this.setState({ error: null, info: null, copied: false })}
              className="inline-flex h-[34px] items-center rounded-md border border-line bg-surface px-3 text-label font-medium text-ink-700 hover:bg-surface-2"
            >
              Try this screen again
            </button>
            <button
              type="button"
              onClick={this.report}
              className="inline-flex h-[34px] items-center gap-1.5 rounded-md border border-line bg-surface px-3 text-label font-medium text-ink-700 hover:bg-surface-2"
            >
              <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
              {copied ? 'Copied' : 'Copy details for support'}
            </button>
          </div>

          <details className="mt-4 border-t border-line pt-3">
            <summary className="cursor-pointer text-label text-ink-500 hover:text-ink-700">
              Technical details
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-surface-2 p-3 font-mono text-micro text-ink-700">
              {error.toString()}
              {info?.componentStack || ''}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
